import { Flex } from 'components/Flex';
import { BKJIcon, BKJLoadingSpinner, BKJSelectSearch } from '@awais4123/fillify-design-library';
import { FC } from 'react';
import { useTranslation } from 'hooks/useTranslation';
import { usePositionsAllocationModalUIContext } from 'pages/SettingsPage/PositionsSettingsPage/B2BPositionsSettingsPage/modals/PositionsByLocation/PositionsAllocationModal.ui.context';
import { P } from 'components/Typography';
import { usePositionsAllocationModalDataContext } from 'pages/SettingsPage/PositionsSettingsPage/B2BPositionsSettingsPage/modals/PositionsByLocation/PositionsAllocationModal.data.context';
import {
  PositionTitle,
  Text,
  BoldText,
  PositionDetailsTableContentCell,
  ModalHeaderSection,
} from 'pages/SettingsPage/PositionsSettingsPage/B2BPositionsSettingsPage/modals/PositionsByLocation/PositionsAllocationModal.styled';
import pluralize from 'pluralize';
import {
  FetchCommunitiesListProvider,
  useFetchCommunitiesListContext,
} from 'pages/SettingsPage/PositionsSettingsPage/B2BPositionsSettingsPage/modals/PositionsByLocation/AssignedLocations.context';

const ApplyPositionComponent: FC = () => {
  const { positionId } = usePositionsAllocationModalUIContext();
  const { data } = usePositionsAllocationModalDataContext();
  const { t } = useTranslation();
  const { data: communities, isLoading, loadMore, hasNextPage } = useFetchCommunitiesListContext();

  const communitiesList = communities?.fetchCommunities?.edges;
  const selectedPosition = data?.positions?.find((pos) => pos.positionId === positionId);
  const sortedCommunitiesList =
    communitiesList &&
    communitiesList.sort((a, b) => {
      const assignedA = selectedPosition?.locationsPerPosition?.some(
        (loc) => a?.node?.id === loc.id,
      );
      const assignedB = selectedPosition?.locationsPerPosition?.some(
        (loc) => b?.node?.id === loc.id,
      );

      if (assignedA && !assignedB) return -1;
      if (!assignedA && assignedB) return 1;
      return 0;
    });
  const selectedPositionCount = sortedCommunitiesList?.filter((loc) =>
    selectedPosition?.locationsPerPosition.some((assignedLoc) => assignedLoc.id === loc.node?.id),
  ).length;

  if (hasNextPage && !isLoading && loadMore) {
    loadMore();
  }

  if (isLoading) return <BKJLoadingSpinner variant="Default" />;
  if (selectedPositionCount === 0)
    return (
      <Flex justifyContent="center" width="100%">
        <Text>{t('common:NO_RESULTS')}</Text>
      </Flex>
    );
  return (
    <Flex flexDirection="column" width="100%">
      <BoldText>
        {selectedPositionCount} &nbsp;
        {pluralize(t('common:LOCATION'), selectedPositionCount)} &nbsp;
        {t('settings:APPLIED')}
      </BoldText>
      {sortedCommunitiesList &&
        sortedCommunitiesList.map((community, index) => {
          const assignedLocation = selectedPosition?.locationsPerPosition.some(
            (loc) => community?.node?.id === loc.id,
          );

          const isGrey = index % 2;
          return (
            <PositionDetailsTableContentCell isGrey={!!isGrey} key={index}>
              <Flex gap="10px" alignItems="center" width="100%">
                {assignedLocation && <BKJIcon iconName="Checkmark" />}
                <Text>{community.node?.name}</Text>
              </Flex>
            </PositionDetailsTableContentCell>
          );
        })}
    </Flex>
  );
};

const ApplyPositionWrapper: FC = () => {
  const { positionId } = usePositionsAllocationModalUIContext();
  const { data } = usePositionsAllocationModalDataContext();
  const { t } = useTranslation();
  const {
    onChange: onSearchChange,
    values,
    isLoading,
    loadMore,
    hasNextPage,
  } = useFetchCommunitiesListContext();

  const selectedPosition = data?.positions?.find((pos) => pos.positionId === positionId);

  if (hasNextPage && !isLoading && loadMore) {
    loadMore();
  }
  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      alignItems="flex-start"
      width="100%"
      flexGrow={1}
    >
      <ModalHeaderSection>
        <PositionTitle>
          {t('shift_modal:POSITION')}: {selectedPosition?.positionName}
        </PositionTitle>
        <P>{t('location_settings:POSITION_ALLOCATION_TEXT')}</P>
      </ModalHeaderSection>
      <Flex flexDirection="column" gap="32px" width="100%" padding="0 16px 24px 16px">
        <BKJSelectSearch
          onChange={onSearchChange}
          name="byName"
          value={values.byName}
          placeholder={t('settings:SEARCH_LOCATIONS')}
        />
      </Flex>
      <ApplyPositionComponent />
    </Flex>
  );
};

export const ApplyPositionView: FC = () => {
  return (
    <FetchCommunitiesListProvider>
      <ApplyPositionWrapper />
    </FetchCommunitiesListProvider>
  );
};
